import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  icon?: React.ReactNode;
  onAddNew?: () => void;
  addNewLabel?: string;
  required?: boolean;
}

function CustomSelect({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  icon,
  onAddNew,
  addNewLabel = '+ Add New',
  required = false,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: string) => {
    if (option === 'add-new' && onAddNew) {
      onAddNew();
      setIsOpen(false);
    } else {
      onChange(option);
      setIsOpen(false);
    }
  };

  return (
    <div className="custom-dropdown" ref={wrapperRef}>
      <div
        className="custom-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="custom-dropdown-value">
          {value || placeholder}
        </div>
        <div className="custom-dropdown-icons">
          {icon && <div className="custom-dropdown-icon-left">{icon}</div>}
          <ChevronDown
            size={18}
            className={`custom-dropdown-chevron ${isOpen ? 'open' : ''}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="custom-dropdown-menu">
          {!value && placeholder && (
            <div className="custom-dropdown-option placeholder" onClick={() => handleSelect('')}>
              {placeholder}
            </div>
          )}
          {options.map((option) => (
            <div
              key={option}
              className={`custom-dropdown-option ${value === option ? 'selected' : ''}`}
              onClick={() => handleSelect(option)}
            >
              {option}
            </div>
          ))}
          {onAddNew && (
            <div
              className="custom-dropdown-option add-new"
              onClick={() => handleSelect('add-new')}
            >
              {addNewLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomSelect;


